import { useLocation } from "react-router-dom";
import { AppRoutes } from "./router";
import { I18nextProvider } from "react-i18next";
import i18n from "./i18n";
import { useEffect, useLayoutEffect, useRef, Suspense } from "react";
import ScrollToTop from "./components/feature/ScrollToTop";
import CookieConsent from "./components/feature/CookieConsent";
import MetaPixelPageViews from "./components/feature/MetaPixelPageViews";
import { AuthProvider } from "./contexts/AuthContext";
import { DemoProvider } from "./contexts/DemoContext";
import RouteErrorBoundary from "./components/feature/RouteErrorBoundary";

// Pages that use a light/white background
const LIGHT_BG_ROUTES = ['/about', '/hub', '/privacy', '/terms', '/q', '/demo'];
const DARK_HUB_ROUTES = ['/hub/login'];
// Routes where navigation should feel instant (no fade animation)
const NO_TRANSITION_ROUTES = ['/hub'];

function PageTransitionWrapper({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const wrapperRef = useRef<HTMLDivElement>(null);

  // useLayoutEffect, not useEffect — this must land before the browser
  // paints the new route. useEffect fires after paint, so for a frame or two
  // the new page would render on top of the previous route's leftover
  // html/body background color, flashing through any rounded/transparent
  // corners (e.g. dark Home bg showing at About's top edges) until it caught
  // up.
  useLayoutEffect(() => {
    // Sync html + body background with the page so overscroll never flickers
    const isDarkHubRoute = DARK_HUB_ROUTES.some((r) => location.pathname.startsWith(r));
    const isLight = LIGHT_BG_ROUTES.some((r) => location.pathname.startsWith(r));
    const bg = isDarkHubRoute ? '#0a0608' : isLight ? '#ffffff' : '#0a0a0a';
    document.documentElement.style.background = bg;
    document.body.style.background = bg;
  }, [location.pathname]);

  useEffect(() => {
    const isHub = NO_TRANSITION_ROUTES.some((r) => location.pathname.startsWith(r));
    if (isHub) return; // hub manages its own internal scroll, not window scroll
    window.scrollTo(0, 0);
  }, [location.pathname]);

  useEffect(() => {
    const isHub = NO_TRANSITION_ROUTES.some((r) => location.pathname.startsWith(r));
    if (isHub) return; // hub sections switch instantly like tabs
    const el = wrapperRef.current;
    if (!el) return;
    el.classList.remove('page-enter');
    void el.offsetWidth;
    el.classList.add('page-enter');
  }, [location.pathname]);

  return (
    <div ref={wrapperRef} className="page-enter">
      {children}
    </div>
  );
}

// Keyed by path so navigating away from a crashed page clears the error
function RoutesWithBoundary() {
  const location = useLocation();
  return (
    <RouteErrorBoundary key={location.pathname}>
      <AppRoutes />
    </RouteErrorBoundary>
  );
}

function App() {
  return (
    <I18nextProvider i18n={i18n}>
      {/* The router comes from the entry point: BrowserRouter in main.tsx,
          StaticRouter in entry-server.tsx (build-time prerender). */}
        <DemoProvider>
          <AuthProvider>
            <PageTransitionWrapper>
              <Suspense fallback={
                <div style={{ minHeight: '100vh', background: '#0a0a0a', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <div style={{ width: 32, height: 32, border: '3px solid rgba(255,107,53,0.9)', borderTopColor: 'transparent', borderRadius: '50%', animation: 'app-boot-spin 0.8s linear infinite' }} />
                  <style>{`@keyframes app-boot-spin{to{transform:rotate(360deg)}}`}</style>
                </div>
              }>
                <RoutesWithBoundary />
              </Suspense>
            </PageTransitionWrapper>
            <ScrollToTop />
            <CookieConsent />
            <MetaPixelPageViews />
          </AuthProvider>
        </DemoProvider>
    </I18nextProvider>
  );
}

export default App;
